import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import fetchWithCredentials from "apiClient";
import { EmptyUnit, Resident } from "types/Resident";
import { parseResidentsListResponse } from "utils/residentsListResponse";

interface ResidentsContextType {
  residents: Resident[];
  emptyUnits: EmptyUnit[];
  loading: boolean;
  error: string | null;
  refresh: (condoId: number | null) => Promise<void>;
  residentsCount: number;
  selectedContactId: number | null;
  setSelectedContactId: (contactId: number | null) => void;
  markContactAsRead: (contactId: number) => void;
  updateLastReadAt: (contactId: number, lastReadAt: string) => void;
}

const ResidentsContext = createContext<ResidentsContextType | undefined>(
  undefined
);

export function ResidentsProvider({ children }: { children: ReactNode }) {
  const [residents, setResidents] = useState<Resident[]>([]);
  const [emptyUnits, setEmptyUnits] = useState<EmptyUnit[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    null
  );

  const fetchResidents = useCallback(async (condoId: number | null) => {
    if (!condoId) {
      setResidents([]);
      setEmptyUnits([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchWithCredentials(
        `/manager-api/residents/?condo=${condoId}`
      );
      const parsed = parseResidentsListResponse(payload);
      setResidents(parsed.residents);
      setEmptyUnits(parsed.emptyUnits);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Error fetching residents");
      setResidents([]);
      setEmptyUnits([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(
    async (condoId: number | null) => {
      await fetchResidents(condoId);
    },
    [fetchResidents]
  );

  const markContactAsRead = useCallback((contactId: number) => {
    setResidents((prev) =>
      prev.map((resident) =>
        resident.contact.id === contactId
          ? { ...resident, hasUnread: false }
          : resident
      )
    );
  }, []);

  const updateLastReadAt = useCallback(
    (contactId: number, lastReadAt: string) => {
      setResidents((prev) =>
        prev.map((resident) =>
          resident.contact.id === contactId
            ? { ...resident, lastReadAt }
            : resident
        )
      );
    },
    []
  );

  const residentsCount = useMemo(() => residents.length, [residents]);

  return (
    <ResidentsContext.Provider
      value={{
        residents,
        emptyUnits,
        loading,
        error,
        refresh,
        residentsCount,
        selectedContactId,
        setSelectedContactId,
        markContactAsRead,
        updateLastReadAt,
      }}
    >
      {children}
    </ResidentsContext.Provider>
  );
}

export function useResidents() {
  const context = useContext(ResidentsContext);
  if (!context) {
    throw new Error("useResidents must be used within a ResidentsProvider");
  }
  return context;
}
